import { Button, Typography } from '@mui/material'
import { useMsal } from '@azure/msal-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { loginRequest } from '../../authConfig'
import RoutesEnum from '../../components/Routes/rootEnum'
import SpinnerBlock from '../../components/Spinner/SpinnerBlock'
import { getCachedIdentityConfig } from './IdentityConfigService'
import PlatformBrandedShell from './PlatformBrandedShell'
import { acceptInvitation, getHrdForEmail, getInvitationPreview } from './invitationApi'
import { IInvitationPreview } from './invitationTypes'
import messages from './invitationMessages'

// E10 F10-3 — the public invitation acceptance landing (rendered by AppShell at /invitation/:token, a
// PUBLIC route inside MsalProvider so MSAL sign-up + the authed accept both work). Flow:
//   1. Anonymously fetch the preview (the invitee is not signed in yet).
//   2. "Accept" -> loginRedirect({ prompt: 'create' }) so the human sets their OWN Entra credential, with
//      state = this page's path so the callback returns here.
//   3. Back here, now authenticated, POST accept (authed — the backend binds the oid from the JWT) and
//      redirect to /persona-splash?personaId= so PersonaGate auto-selects the new persona.
// A sessionStorage flag set before the redirect makes step 3 auto-run on return; a direct visit while
// already signed in instead waits for an explicit click.
const ACCEPT_PENDING_KEY = 'invitationAcceptToken'

interface AcceptInvitationPageProps {
  token: string
}

const AcceptInvitationPage = ({ token }: AcceptInvitationPageProps) => {
  const { formatMessage } = useIntl()
  const { instance } = useMsal()
  const navigate = useNavigate()

  const [preview, setPreview] = useState<IInvitationPreview | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [invalid, setInvalid] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState(false)
  const acceptStarted = useRef(false)

  const isAuthenticated = !!instance.getActiveAccount()

  // Anonymous preview — works before sign-in. A 404 (unknown) / 410 (expired) renders the invalid state.
  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    getInvitationPreview(token, controller.signal)
      .then((p) => {
        if (!cancelled) {
          setPreview(p)
          setLoadingPreview(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInvalid(true)
          setLoadingPreview(false)
        }
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [token])

  const doAccept = useCallback(async () => {
    if (acceptStarted.current) return
    acceptStarted.current = true
    setAcceptError(false)
    setAccepting(true)
    try {
      const result = await acceptInvitation(token, instance)
      sessionStorage.removeItem(ACCEPT_PENDING_KEY)
      navigate(`${RoutesEnum.PERSONA_SPLASH}?personaId=${result.userId}`, { replace: true })
    } catch {
      sessionStorage.removeItem(ACCEPT_PENDING_KEY)
      acceptStarted.current = false
      setAccepting(false)
      setAcceptError(true)
    }
  }, [navigate, token, instance])

  // Auto-accept once the sign-up redirect returns here authenticated (the pending flag matches this token).
  useEffect(() => {
    if (isAuthenticated && sessionStorage.getItem(ACCEPT_PENDING_KEY) === token && !acceptStarted.current) {
      void doAccept()
    }
  }, [isAuthenticated, token, doAccept])

  // Not signed in: route by home realm (E20 20.5.1 / defect D7). A federated invitee is sent through
  // their organisation's IdP with domain_hint — prompt=create would mint a LOCAL credential and guarantee
  // a duplicate shadow user on their first federated sign-in. Everyone else keeps the existing create
  // path, and ANY uncertainty (hrd failure, kind local, provider missing from the cached config) falls
  // back to it too — the routing lookup must never block the acceptance journey. The pending flag tells
  // the returned page to auto-accept in both branches.
  //
  // The hrd lookup opens an async window between click and redirect that the old synchronous
  // loginRedirect never had — guard re-entry and show the spinner (accepting unmounts the button), or a
  // second click races a duplicate lookup + a second loginRedirect (MSAL interaction_in_progress).
  const redirectStarted = useRef(false)
  const startSignUpRedirect = async () => {
    if (redirectStarted.current) return
    redirectStarted.current = true
    setAccepting(true)
    sessionStorage.setItem(ACCEPT_PENDING_KEY, token)
    try {
      if (preview?.email) {
        const hrd = await getHrdForEmail(preview.email)
        if (hrd.kind === 'federation' && hrd.providerId != null) {
          const provider = getCachedIdentityConfig()?.federationProviders.find(
            (p) => p.id === hrd.providerId,
          )
          if (provider) {
            void instance.loginRedirect({
              ...loginRequest,
              extraQueryParameters: {
                ...loginRequest.extraQueryParameters,
                domain_hint: provider.domainHint,
              },
              state: `/invitation/${token}`,
            })
            return
          }
        }
      }
    } catch {
      // Fall through to the local create path.
    }
    void instance.loginRedirect({
      ...loginRequest,
      prompt: 'create',
      state: `/invitation/${token}`,
    })
  }

  const onAccept = () => {
    if (isAuthenticated) {
      void doAccept()
      return
    }
    void startSignUpRedirect()
  }

  if (loadingPreview || accepting) {
    return <SpinnerBlock />
  }

  // acceptError is checked BEFORE invalid: on the auto-accept return path the anonymous preview and the
  // authed accept can both fail (e.g. a transient blip) — a retry-able accept failure must win over the
  // dead-end "invalid link" screen so the user isn't stranded without a Try-again on a still-valid token.
  if (acceptError) {
    return (
      <PlatformBrandedShell>
        <Typography variant='h5' textAlign='center'>{formatMessage(messages.errorHeading)}</Typography>
        <Typography color='text.secondary' textAlign='center'>{formatMessage(messages.errorBody)}</Typography>
        <Button variant='contained' onClick={onAccept}>
          {formatMessage(messages.retry)}
        </Button>
      </PlatformBrandedShell>
    )
  }

  if (invalid) {
    return (
      <PlatformBrandedShell>
        <Typography variant='h5' textAlign='center'>{formatMessage(messages.invalidHeading)}</Typography>
        <Typography color='text.secondary' textAlign='center'>{formatMessage(messages.invalidBody)}</Typography>
      </PlatformBrandedShell>
    )
  }

  return (
    <PlatformBrandedShell>
      <Typography variant='h5' textAlign='center'>
        {formatMessage(messages.heading, { workspace: preview?.tenantDisplayName ?? '' })}
      </Typography>
      <Typography color='text.secondary' textAlign='center'>
        {formatMessage(messages.subheading, { email: preview?.email ?? '' })}
      </Typography>
      <Button variant='contained' onClick={onAccept}>
        {formatMessage(isAuthenticated ? messages.acceptButtonSignedIn : messages.acceptButton)}
      </Button>
    </PlatformBrandedShell>
  )
}

export default AcceptInvitationPage
